import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import StockMasterHeader from "./Components/StockMasterHeader";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, TablePagination, Checkbox, IconButton, Snackbar, Alert, Skeleton,
} from "@mui/material";
import { MdDelete, MdVisibility, MdEdit, MdUpload, MdEditNote } from "react-icons/md";
import BulkImportItems from "./Components/BulkImportItems";
import BulkEditItems from "./Components/BulkEditItems";

const StockMaster = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [importOpen, setImportOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      let url = `/api/getAllInventoryItems?page=${page + 1}&limit=${rowsPerPage}`;
      if (categoryFilter) url += `&category=${categoryFilter}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok) { setItems(data.items || []); setTotalCount(data.totalCount || 0); }
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch("/api/getAllInventoryCategories", { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok) setCategories(data.categories || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => { fetchItems(); }, [page, rowsPerPage, categoryFilter, search]);

  const handleDelete = async () => {
    if (selected.length === 0) return;
    try {
      const res = await fetch("/api/deleteInventoryItem", {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: selected }),
      });
      const data = await res.json();
      if (res.ok) {
        setSnackbar({ open: true, message: `${data.deletedCount} item(s) deleted`, severity: "success" });
        setSelected([]); fetchItems();
      } else { setSnackbar({ open: true, message: data.error, severity: "error" }); }
    } catch (err) { setSnackbar({ open: true, message: "Failed to delete", severity: "error" }); }
  };

  const getStockStatus = (item) => {
    if (item.minThreshold > 0 && item.currentStock <= item.minThreshold)
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Low Stock</span>;
    if (item.maxThreshold > 0 && item.currentStock >= item.maxThreshold)
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Overstocked</span>;
    return <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">In Stock</span>;
  };

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      <StockMasterHeader />
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <input type="text" placeholder="Search items..." value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-64" />
        <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="">All Categories</option>
          {categories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
        {selected.length > 0 && (
          <>
            <button onClick={() => setBulkEditOpen(true)} className="flex items-center gap-1 bg-amber-500 text-white px-3 py-2 rounded-lg text-sm hover:bg-amber-600">
              <MdEditNote /> Bulk Edit ({selected.length})
            </button>
            <button onClick={handleDelete} className="flex items-center gap-1 bg-red-500 text-white px-3 py-2 rounded-lg text-sm hover:bg-red-600">
              <MdDelete /> Delete ({selected.length})
            </button>
          </>
        )}
        <button onClick={() => setImportOpen(true)} className="ml-auto flex items-center gap-1 border border-primary text-primary px-3 py-2 rounded-lg text-sm hover:bg-blue-50">
          <MdUpload /> Bulk Import
        </button>
      </div>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                <TableCell padding="checkbox">
                  <Checkbox indeterminate={selected.length > 0 && selected.length < items.length}
                    checked={items.length > 0 && selected.length === items.length}
                    onChange={(e) => setSelected(e.target.checked ? items.map((i) => i._id) : [])} />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Item Code</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Stock</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Unit Cost (Nu)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Total Value (Nu)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? [...Array(5)].map((_, i) => (
                <TableRow key={i}>{[...Array(11)].map((_, j) => <TableCell key={j}><Skeleton animation="wave" /></TableCell>)}</TableRow>
              )) : items.length === 0 ? (
                <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4 }}><p className="text-gray-400">No items found</p></TableCell></TableRow>
              ) : items.map((item) => (
                <TableRow key={item._id} hover>
                  <TableCell padding="checkbox"><Checkbox checked={selected.includes(item._id)} onChange={() => setSelected((p) => p.includes(item._id) ? p.filter((s) => s !== item._id) : [...p, item._id])} /></TableCell>
                  <TableCell><span className="font-medium text-primary">{item.sku}</span></TableCell>
                  <TableCell><span className="font-medium text-gray-700">{item.itemCode || "-"}</span></TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.category?.name || "-"}</TableCell>
                  <TableCell className="capitalize">{item.unitOfMeasure}</TableCell>
                  <TableCell><span className="font-medium">{item.currentStock}</span></TableCell>
                  <TableCell>{item.unitCost?.toLocaleString()}</TableCell>
                  <TableCell>{(item.currentStock * item.unitCost)?.toLocaleString()}</TableCell>
                  <TableCell>{getStockStatus(item)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <IconButton size="small" onClick={() => navigate("/StockMaster/View/" + item._id)}><MdVisibility className="text-primary" /></IconButton>
                      <IconButton size="small" onClick={() => navigate("/StockMaster/Edit", { state: { item } })}><MdEdit className="text-gray-500" /></IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={totalCount} page={page}
          onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]} />
      </Paper>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
      </Snackbar>

      <BulkImportItems
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onSuccess={() => { fetchItems(); setSnackbar({ open: true, message: "Import complete", severity: "success" }); }}
      />
      <BulkEditItems
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        selectedIds={selected}
        onSuccess={(data) => {
          setSnackbar({ open: true, message: `${data.modifiedCount} item(s) updated`, severity: "success" });
          setSelected([]); fetchItems();
        }}
      />
    </div>
  );
};

export default StockMaster;
